import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import {
  DEAL_TEAM_ROLES,
  DEAL_TEAM_ROLE_HINTS,
  dealTeamRoleLabel,
  type DealTeamRole,
} from "@shared/crm-team";
import { ArrowRightLeft, Check, Search } from "lucide-react";

/** Cor por papel — o mesmo selo aparece no funil, então precisa ser estável. */
export const ROLE_TINT: Record<DealTeamRole, string> = {
  sdr: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300",
  hunter: "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300",
  closer: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  pre_vendas: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300",
  cs: "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300",
  gerente: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  parceiro: "bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300",
};

export function DealTeamRoleBadge({ role, className = "" }: { role: string; className?: string }) {
  return (
    <Badge className={`${ROLE_TINT[role as DealTeamRole] ?? ROLE_TINT.parceiro} text-[11px] ${className}`}>
      {dealTeamRoleLabel(role)}
    </Badge>
  );
}

export function RoleSelect({
  value,
  onChange,
  placeholder = "Escolha o papel",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {DEAL_TEAM_ROLES.map((r) => (
          <SelectItem key={r} value={r}>
            <span className="font-medium">{dealTeamRoleLabel(r)}</span>
            <span className="text-muted-foreground"> — {DEAL_TEAM_ROLE_HINTS[r]}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Papel em botões pequenos, logo embaixo do nome da pessoa. */
function RoleChips({
  value,
  onChange,
  size = "sm",
}: {
  value: string;
  onChange: (v: string) => void;
  size?: "sm" | "xs";
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {DEAL_TEAM_ROLES.map((r) => (
        <button
          key={r}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange(r);
          }}
          className={`rounded-full border transition-colors ${
            size === "xs" ? "text-[10px] px-1.5 py-px" : "text-[11px] px-2 py-0.5"
          } ${
            value === r
              ? "border-primary bg-primary text-primary-foreground font-medium"
              : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
          }`}
        >
          {dealTeamRoleLabel(r)}
        </button>
      ))}
    </div>
  );
}

function iniciais(nome: string | null | undefined) {
  if (!nome) return "?";
  return nome.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");
}

function primeiroNome(nome: string | null | undefined) {
  return (nome ?? "").trim().split(/\s+/)[0] || "";
}

/** Motivos mais comuns — um clique em vez de digitar. */
const MOTIVOS_RAPIDOS = [
  "Reunião marcada, closer assume",
  "Lead qualificado",
  "Redistribuição da carteira",
  "Férias / afastamento",
  "Saiu da empresa",
];

/** A partir de quantas pessoas a lista ganha busca. */
const LIMITE_SEM_BUSCA = 6;

/**
 * Passagem de bastão de um comercial para outro.
 *
 * É o MESMO diálogo em todo lugar (cabeçalho do deal, card do funil, aba Time)
 * para que transferir seja sempre a mesma coisa: quem entrega fica no time com
 * o papel dele (SDR, hunter…), quem recebe entra com o dele (closer…), o
 * responsável passa para quem recebe e a passagem entra no histórico com o
 * motivo. Os papéis ficam em destaque de propósito — "quem foi o SDR e quem é
 * o closer desse contato" é a pergunta que o relatório responde depois.
 *
 * Segue o desenho da Agenda (BlockDialog/MeetingSheet): escolhas em cartões e
 * chips, motivos rápidos e a frase-resumo no rodapé — é ela que pega o
 * "transferi para a pessoa errada" antes do clique.
 *
 * Carrega o deal e o time sozinho: o card do funil não tem esses dados.
 */
export default function TransferDealDialog({
  dealId,
  open,
  onClose,
  onDone,
}: {
  dealId: number;
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
}) {
  const utils = trpc.useUtils();
  const [toUserId, setToUserId] = useState<number | null>(null);
  const [toRole, setToRole] = useState<string>("closer");
  const [fromRole, setFromRole] = useState<string>("");
  const [reason, setReason] = useState("");
  const [busca, setBusca] = useState("");
  const [changeOwner, setChangeOwner] = useState(true);

  const dealQuery = trpc.crm.deals.get.useQuery({ id: dealId }, { enabled: open });
  const teamQuery = trpc.crm.deals.listTeam.useQuery({ dealId }, { enabled: open });
  const usersQuery = trpc.crm.deals.listAssignableUsers.useQuery(undefined, { enabled: open });

  const deal = dealQuery.data?.deal ?? null;
  const owner = dealQuery.data?.owner ?? null;
  const ownerUserId = deal?.ownerUserId ?? null;
  const team = teamQuery.data ?? [];
  // Papel que quem entrega já tem registrado — evita o usuário ter que lembrar.
  const papelAtualDoDono: string | undefined = team.find(
    (r: any) => r.member.userId === ownerUserId,
  )?.member.role;

  // Sugere SDR → closer, que é a passagem mais comum. Se quem entrega já tem
  // papel no time, respeita. Limpa o resto ao abrir.
  useEffect(() => {
    if (!open) return;
    setFromRole(papelAtualDoDono ?? "sdr");
    setToRole("closer");
    setToUserId(null);
    setReason("");
    setBusca("");
    setChangeOwner(true);
  }, [open, papelAtualDoDono]);

  const transfer = trpc.crm.deals.transfer.useMutation({
    onSuccess: () => {
      toast.success("Negociação transferida");
      utils.crm.deals.get.invalidate({ id: dealId });
      utils.crm.deals.listTeam.invalidate({ dealId });
      utils.crm.deals.listTransfers.invalidate({ dealId });
      utils.crm.deals.listActivities.invalidate({ dealId });
      utils.crm.deals.kanban.invalidate();
      utils.crm.deals.list.invalidate();
      onDone?.();
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao transferir")),
  });

  const candidatos = useMemo(
    () => (usersQuery.data ?? []).filter((u: any) => u.id !== ownerUserId),
    [usersQuery.data, ownerUserId],
  );
  const comBusca = candidatos.length > LIMITE_SEM_BUSCA;
  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return candidatos;
    return candidatos.filter((u: any) =>
      [u.name, u.email, u.jobTitle].some((v) => (v ?? "").toLowerCase().includes(q)),
    );
  }, [candidatos, busca]);
  const destinatario = candidatos.find((u: any) => u.id === toUserId) ?? null;

  // Uma linha, para conferir antes do clique.
  const resumo = useMemo(() => {
    if (!destinatario) return "";
    const para = primeiroNome(destinatario.name) || destinatario.email;
    const quem = owner ? `${primeiroNome(owner.name)} → ${para}` : `→ ${para}`;
    const papeis = owner && fromRole
      ? `${dealTeamRoleLabel(fromRole)} → ${dealTeamRoleLabel(toRole)}`
      : dealTeamRoleLabel(toRole);
    const dono = changeOwner ? `${para} vira o responsável` : `${owner ? primeiroNome(owner.name) : "responsável"} continua responsável`;
    return `${quem} · ${papeis} · ${dono}.`;
  }, [destinatario, toRole, owner, fromRole, changeOwner]);

  const ocupado = transfer.isPending;

  function salvar() {
    if (!toUserId || !toRole || ocupado) return;
    transfer.mutate({
      dealId,
      toUserId,
      toRole: toRole as DealTeamRole,
      fromRole: (owner ? fromRole || null : null) as DealTeamRole | null,
      reason: reason.trim() || null,
      changeOwner,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg bg-white dark:bg-card max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4" />
            Transferir negociação
          </DialogTitle>
          <DialogDescription className="truncate">
            {deal?.title ?? "Carregando…"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* ── Quem entrega ──────────────────────────────────────────── */}
          {owner && (
            <div>
              <Label className="text-xs">Responsável atual</Label>
              <div className="mt-1.5 flex items-start gap-2.5 rounded-lg border bg-muted/40 px-2.5 py-2">
                <span className="h-9 w-9 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                  {iniciais(owner.name)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate leading-tight">{owner.name}</p>
                  <p className="text-[11px] text-muted-foreground mb-1.5">Papel nesta negociação</p>
                  <RoleChips value={fromRole} onChange={setFromRole} size="xs" />
                </div>
              </div>
            </div>
          )}

          {/* ── Quem recebe ───────────────────────────────────────────── */}
          <div>
            <Label className="text-xs">Transferir para</Label>
            {comBusca && (
              <div className="relative mt-1.5">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  className="h-9 pl-8"
                  placeholder="Buscar pelo nome ou cargo"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>
            )}
            <div className="mt-1.5 space-y-1.5 max-h-64 overflow-y-auto pr-0.5">
              {usersQuery.isLoading && (
                <p className="text-xs text-muted-foreground">Carregando equipe…</p>
              )}
              {!usersQuery.isLoading && visiveis.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {busca ? "Ninguém com esse nome." : "Ninguém mais disponível para receber."}
                </p>
              )}
              {visiveis.map((u: any) => {
                const ativo = u.id === toUserId;
                return (
                  <div
                    key={u.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setToUserId(u.id)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setToUserId(u.id); } }}
                    className={`flex items-start gap-2.5 rounded-lg border px-2.5 py-2 cursor-pointer transition-colors ${
                      ativo ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                    }`}
                  >
                    <span
                      className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
                        ativo ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"
                      }`}
                    >
                      {ativo ? <Check className="h-4 w-4" /> : iniciais(u.name ?? u.email)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate leading-tight ${ativo ? "text-primary" : ""}`}>
                        {u.name ?? u.email}
                      </p>
                      {ativo ? (
                        <>
                          <p className="text-[11px] text-muted-foreground mb-1.5">Vai entrar como</p>
                          <RoleChips value={toRole} onChange={setToRole} size="xs" />
                        </>
                      ) : (
                        <p className="text-[11px] text-muted-foreground truncate">{u.jobTitle ?? u.email}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Motivo ────────────────────────────────────────────────── */}
          <div>
            <Label className="text-xs">Motivo</Label>
            <Input
              className="mt-1 h-9"
              placeholder="Opcional"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") salvar(); }}
            />
            <div className="mt-1.5 flex flex-wrap gap-1">
              {MOTIVOS_RAPIDOS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setReason(m)}
                  className={`text-[11px] rounded-full border px-2 py-0.5 transition-colors ${
                    reason === m
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {resumo && (
            <p className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground leading-relaxed">
              {resumo}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {owner ? (
            <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-muted-foreground">
              <Checkbox
                checked={!changeOwner}
                onCheckedChange={(v) => setChangeOwner(v !== true)}
              />
              Manter {primeiroNome(owner.name)} como responsável
            </label>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={ocupado}>
            Cancelar
          </Button>
          <Button size="sm" disabled={!toUserId || !toRole || ocupado} onClick={salvar}>
            <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
            {ocupado ? "Transferindo…" : "Transferir"}
          </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
